import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useEventLogging } from '../../../../../models/Logging';
import { getDisplayName } from '../../../../../models/NameUtils';
import { getDefaultDateStringValue, getDefaultTimeStringValue } from '../../../../../models/UiUtils';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useEmailSendNowMutation } from '../../../../../queries';
import { brandPrimaryHover } from '../../../../styles/colors';
import { baseStyleSheet } from '../../../../styles/styles';
import { PopoverType, TinyPopover } from '../../../TinyPopover';
import { DangerIcon } from '../../../svgs/icons/DangerIcon';
import { ExpiryIcon } from '../../../svgs/icons/ExpiryIcon';
import { InfoIcon } from '../../../svgs/icons/InfoIcon';
import { SendSpeedDisplay } from '../SendSpeedDisplay';
import { styleSheet } from './styles';

const getDetailedStatusMessage = (status: Api.IEmailMessageDetailedStatus, campaign: Api.ICampaign) => {
	switch (status?.statusCode) {
		case Api.EmailDetailedStatusCode.Unknown:
			return 'Please contact support@levitate.ai';
		case Api.EmailDetailedStatusCode.BusinessHourRestriction:
			return `Your account is currently outside of business hours and will resume at ${getDefaultTimeStringValue(
				status?.approxmiateNextSendAttempt
			)}.`;
		case Api.EmailDetailedStatusCode.CapacityRestriction:
			return `Your account hit its hourly send quota. Your campaign will resume at ${getDefaultTimeStringValue(
				status?.approxmiateNextSendAttempt
			)}.`;
		case Api.EmailDetailedStatusCode.EmailProviderReconnect:
			return 'Please reconnect your email in order to resume this campaign.';
		case Api.EmailDetailedStatusCode.Sending:
			return '';
		case Api.EmailDetailedStatusCode.Cancelled:
			return `This campaign was cancelled on ${getDefaultDateStringValue(
				status.statusDate
			)} at ${getDefaultTimeStringValue(status.statusDate)}`;
		case Api.EmailDetailedStatusCode.EmailProviderReconnect_Cancelled:
			return '';
		case Api.EmailDetailedStatusCode.Complete:
			return 'complete';
		case Api.EmailDetailedStatusCode.Expired_Complete:
			return `Some of this campaign was automatically cancelled after ${getDefaultDateStringValue(
				campaign?.schedule?.expirationDate
			)}`;
		default:
			break;
	}
};

interface IDisplayProps {
	campaign?: Api.ICampaign;
	className?: string;
	color: StyleDeclarationValue;
	date: Date;
	detailedStatus?: Api.IEmailMessageDetailedStatus;
	status: string;
	styles?: StyleDeclarationValue[];
	userRef?: Api.IPrincipal;
	datePrefix?: string;
	onSendNowSuccess?: (campaign: Api.ICampaign) => void;
}

export const GroupEmailTableRowStatusDisplay: React.FC<IDisplayProps> = ({
	campaign,
	styles,
	className,
	date,
	userRef,
	status,
	color,
	detailedStatus,
	datePrefix = 'on',
	onSendNowSuccess,
}) => {
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging();
	const [isOpen, setOpen] = React.useState(false);
	const message = getDetailedStatusMessage(detailedStatus, campaign);
	const isReconnect = detailedStatus?.statusCode === Api.EmailDetailedStatusCode.EmailProviderReconnect;
	const formattedExpirationDate = new Date(campaign?.schedule?.expirationDate);
	const emailSendNowMutation = useEmailSendNowMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('SendCampaignImmediately-Error', error);
		},
		onSuccess: onSendNowSuccess,
	});
	const sendNow = () => {
		setOpen(false);
		if (campaign?.id) {
			emailSendNowMutation.mutate({ id: campaign.id });
		}
	};

	return (
		<div
			className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}
			onMouseLeave={() => setOpen(false)}
		>
			<div>
				<div className={css(baseStyleSheet.truncateText)}>
					<span className={css(styleSheet.status, color)}>{status}</span>
					{moment(date).isValid() && (
						<>
							{` ${datePrefix} `}
							<span className={css(baseStyleSheet.fontBold, styleSheet.date)}>
								{getDefaultDateStringValue(moment(date))}
							</span>
							{' at '}
							{getDefaultTimeStringValue(moment(date))}
						</>
					)}
				</div>
				{!!userRef && <div>by {getDisplayName(userRef)}</div>}
				<SendSpeedDisplay campaign={campaign} />
				{!!campaign?.schedule?.expirationDate && campaign.status !== Api.EmailSendStatus.Cancelled && (
					<div className={css(styleSheet.expirationDateContainer)}>
						<ExpiryIcon className={css(styleSheet.expirationDateIcon)} />
						<span className={css(styleSheet.expirationDate)}>
							{`This email automatically stops sending after ${formattedExpirationDate.toLocaleDateString()}.`}
						</span>
					</div>
				)}
			</div>
			{!!message && (
				<TinyPopover
					anchor={
						<div
							className={css(
								baseStyleSheet.flex,
								baseStyleSheet.itemsCenter,
								baseStyleSheet.whiteSpaceNoWrap,
								styleSheet.detailed,
								isReconnect && styleSheet.reconnect
							)}
							onMouseEnter={() => setOpen(true)}
						>
							{isReconnect ? (
								<DangerIcon />
							) : (
								<InfoIcon className={css(styleSheet.icon)} fillColor={brandPrimaryHover} />
							)}
							{isReconnect ? 'Action Needed' : 'Campaign Status'}
						</div>
					}
					anchorStyles={[styleSheet.detailed]}
					dismissOnOutsideAction={true}
					autoCloseOtherPopoversOnHover={true}
					type={isReconnect ? PopoverType.errorSecondary : PopoverType.background}
					isOpen={isOpen}
					placement={['right', 'bottom', 'top']}
					align='center'
				>
					<div className={css(styleSheet.popoverContent)}>
						{message}
						{detailedStatus?.statusCode === Api.EmailDetailedStatusCode.BusinessHourRestriction && (
							<button className={css(baseStyleSheet.ctaButtonSmall, styleSheet.sendNowButton)} onClick={sendNow}>
								<span>Send now</span>
							</button>
						)}
					</div>
				</TinyPopover>
			)}
		</div>
	);
};
